import { useState } from "react";
import styled from "@emotion/styled";
import { Flex, Text } from "styles/utility-components";

import Badge from "components/ui/badge/Badge";
import Button from "components/ui/button/Button";
import ConfirmModal from "components/ui/modal/ConfirmModal";
import Modal from "components/ui/modal/Modal";
import SkeletonText from "components/ui/skeleton/SkeletonText";
import { toast } from "components/ui/toast/Toast";
import Tooltip from "components/ui/tooltip/Tooltip";
import { useSession } from "lib/auth/authClient";
import {
  BetterAuthListedSession,
  useListSessions,
  useRevokeOtherSessions,
  useRevokeSession,
} from "lib/hooks/api/auth/useSessions";
import { compactIpAddress } from "lib/ipAddress";
import { settingsRoutes } from "lib/routes";
import { describeUserAgent } from "lib/userAgent";
import { performLogoutToLogin } from "lib/userUtils";
import { formatDate, formatDateTime } from "lib/utils";
import { toHumanRelativeTimeFromNow } from "lib/utils/relative_time";

// Each device keeps a cached copy of its session for up to 5 minutes, so revoking isn't instant.
export const REVOKE_DELAY_MESSAGE = "It may take up to 5 minutes for that device to be signed out.";
export const REVOKE_OTHERS_DELAY_MESSAGE =
  "It may take up to 5 minutes for those devices to be signed out.";

// Another device's page load this recent counts as active now. The current device always is.
const ACTIVE_NOW_MS = 5 * 60 * 1000;

const regionNames = new Intl.DisplayNames(["en-GB"], { type: "region" });

/** "United Kingdom" for "GB", or the code itself when it isn't a known region. */
export function countryName(code: string): string {
  try {
    return regionNames.of(code.toUpperCase()) ?? code;
  } catch {
    return code;
  }
}

/** When the session was last used, falling back to when it signed in. */
function lastUsedAt(session: BetterAuthListedSession): Date {
  return session.lastActiveAt ?? session.createdAt;
}

/** The current session first, then the most recently used. */
function sortSessions(
  sessions: BetterAuthListedSession[],
  isCurrent: (s: BetterAuthListedSession) => boolean,
) {
  return [...sessions].sort((a, b) => {
    if (isCurrent(a) !== isCurrent(b)) return isCurrent(a) ? -1 : 1;
    return lastUsedAt(b).getTime() - lastUsedAt(a).getTime();
  });
}

/** "Last active 3 hours ago · France", or undefined when another device has no recorded activity. */
function describeLastActive(
  session: BetterAuthListedSession,
  isCurrent: boolean,
): string | undefined {
  const { lastActiveAt, lastActiveCountry } = session;
  let when: string;
  if (isCurrent || (lastActiveAt && Date.now() - lastActiveAt.getTime() < ACTIVE_NOW_MS)) {
    when = "Active now";
  } else if (lastActiveAt) {
    when = `Last active ${toHumanRelativeTimeFromNow(lastActiveAt)}`;
  } else {
    return undefined;
  }
  return lastActiveCountry ? `${when} · ${countryName(lastActiveCountry)}` : when;
}

/** A date label that shows the exact time on hover. */
function DateWithTime({ value, children }: { value: Date; children: React.ReactNode }) {
  return (
    <Tooltip content={formatDateTime(value)}>
      <span>{children}</span>
    </Tooltip>
  );
}

// Keyed by the auth-service's `session.login_method` values.
const LOGIN_METHODS: Record<string, { name: string; phrase: string }> = {
  password: { name: "Password", phrase: "with a password" },
  email: { name: "Email link", phrase: "with an email link" },
  google: { name: "Google", phrase: "with Google" },
  microsoft: { name: "Microsoft", phrase: "with Microsoft" },
  enterprise_sso: { name: "Single sign-on (SSO)", phrase: "with SSO" },
  impersonation: { name: "Impersonated by a Stotles admin", phrase: "by a Stotles admin" },
};

function loginMethodOf(session: BetterAuthListedSession) {
  return session.loginMethod ? LOGIN_METHODS[session.loginMethod] : undefined;
}

/** "Signed in with Google on 1 Sept, 2026", leaving out the method when it wasn't recorded. */
function describeSignIn(session: BetterAuthListedSession): string {
  const date = formatDate(session.createdAt);
  const method = loginMethodOf(session);
  return method ? `Signed in ${method.phrase} on ${date}` : `Signed in ${date}`;
}

function SessionDetailsModal({
  session,
  open,
  onOpenChange,
}: {
  session: BetterAuthListedSession | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const method = session && loginMethodOf(session);
  return (
    <Modal open={open} onOpenChange={onOpenChange} maxWidth="420px" padding={24}>
      {session && (
        <Flex column gap={16}>
          <Text h3>{describeUserAgent(session.userAgent)}</Text>
          <Details>
            <dt>Signed in</dt>
            <dd>{formatDateTime(session.createdAt)}</dd>
            {session.signInCountry && (
              <>
                <dt>Signed in from</dt>
                <dd>{countryName(session.signInCountry)}</dd>
              </>
            )}
            {method && (
              <>
                <dt>Sign-in method</dt>
                <dd>{method.name}</dd>
              </>
            )}
            {session.ipAddress && (
              <>
                <dt>IP address at sign-in</dt>
                <dd>{compactIpAddress(session.ipAddress)}</dd>
              </>
            )}
            {session.lastActiveAt && (
              <>
                <dt>Last active</dt>
                <dd>{formatDateTime(session.lastActiveAt)}</dd>
              </>
            )}
            {session.lastActiveCountry && (
              <>
                <dt>Last active from</dt>
                <dd>{countryName(session.lastActiveCountry)}</dd>
              </>
            )}
            {session.lastActiveIpAddress && (
              <>
                <dt>Last IP address</dt>
                <dd>{compactIpAddress(session.lastActiveIpAddress)}</dd>
              </>
            )}
          </Details>
        </Flex>
      )}
    </Modal>
  );
}

function SessionRow({
  session,
  isCurrent,
  onRevoke,
  onViewDetails,
}: {
  session: BetterAuthListedSession;
  isCurrent: boolean;
  onRevoke: () => void;
  onViewDetails: () => void;
}) {
  const device = describeUserAgent(session.userAgent);
  const lastActive = describeLastActive(session, isCurrent);

  return (
    <Row aria-label={device}>
      <Flex column gap={2} minWidth={0} flexGrow={1}>
        <Flex gap={8} alignItems="center">
          <Text bold>{device}</Text>
          {isCurrent && (
            <Badge badgeType="success" fitContent>
              This device
            </Badge>
          )}
        </Flex>
        {lastActive && (
          <Text description color="sysTextSecondary">
            {session.lastActiveAt ? (
              <DateWithTime value={session.lastActiveAt}>{lastActive}</DateWithTime>
            ) : (
              lastActive
            )}
          </Text>
        )}
        <Text description color="sysTextSecondary">
          <DateWithTime value={session.createdAt}>{describeSignIn(session)}</DateWithTime>
        </Text>
      </Flex>
      {!isCurrent && (
        <Button buttonType="secondary" buttonStyle="destructive" onClick={onRevoke}>
          Revoke
        </Button>
      )}
      <Button buttonType="secondary" onClick={onViewDetails}>
        Details
      </Button>
    </Row>
  );
}

function SessionList({
  onRevoke,
  onViewDetails,
}: {
  onRevoke: (session: BetterAuthListedSession) => void;
  onViewDetails: (session: BetterAuthListedSession) => void;
}) {
  const { data: current } = useSession();
  const { data: sessions, isLoading, isError } = useListSessions();

  if (isLoading) {
    return (
      <List aria-label="Loading sessions">
        {[0, 1].map((i) => (
          <Row key={i}>
            <Flex column gap={2}>
              <SkeletonText width="180px" />
              <SkeletonText width="260px" fontSize={12} />
            </Flex>
          </Row>
        ))}
      </List>
    );
  }

  if (sessions === null) {
    return (
      <Flex column gap={8} alignItems="flex-start">
        <Text color="sysTextSecondary">
          For your security, you need to log in again to see your sessions list. You can still sign
          out all other sessions without logging in again.
        </Text>
        <Button
          buttonType="secondary"
          onClick={() => void performLogoutToLogin(settingsRoutes.account)}
        >
          Log in again
        </Button>
      </Flex>
    );
  }

  if (isError || !sessions?.length) {
    return (
      <Text color="sysDestructiveDefault">
        We couldn't load your sessions. Please refresh the page to try again.
      </Text>
    );
  }

  const isCurrent = (session: BetterAuthListedSession) =>
    current?.session
      ? session.token === current.session.token || session.id === current.session.id
      : false;

  return (
    <List>
      {sortSessions(sessions, isCurrent).map((session) => (
        <SessionRow
          key={session.id}
          session={session}
          isCurrent={isCurrent(session)}
          onRevoke={() => onRevoke(session)}
          onViewDetails={() => onViewDetails(session)}
        />
      ))}
    </List>
  );
}

/** Where the user is signed in, with a way to sign any other device out. */
function ActiveSessionsSection(): JSX.Element {
  const [revoking, setRevoking] = useState<BetterAuthListedSession | "others" | null>(null);
  // Kept apart from `revoking` so the message doesn't change while the modal animates closed.
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [viewing, setViewing] = useState<BetterAuthListedSession | null>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const { data: sessions, isLoading: isLoadingSessions } = useListSessions();
  const { mutate: revokeSession } = useRevokeSession();
  const { mutate: revokeOtherSessions, isLoading: isRevokingOthers } = useRevokeOtherSessions();

  // Unknown when the list couldn't load or needs a fresh login, in which case still offer it.
  const hasOtherSessions = !isLoadingSessions && (!sessions || sessions.length > 1);

  const askToRevoke = (target: BetterAuthListedSession | "others") => {
    setRevoking(target);
    setIsConfirmOpen(true);
  };

  const viewDetails = (session: BetterAuthListedSession) => {
    setViewing(session);
    setIsDetailsOpen(true);
  };

  const confirmRevoke = () => {
    if (revoking === "others") {
      revokeOtherSessions(undefined, {
        onSuccess: () =>
          toast.success(`Signed out your other sessions. ${REVOKE_OTHERS_DELAY_MESSAGE}`),
        onError: (error) => toast.error(error.message),
      });
    } else if (revoking) {
      revokeSession(
        { token: revoking.token },
        {
          onSuccess: () => toast.success(`Session revoked. ${REVOKE_DELAY_MESSAGE}`),
          onError: (error) => toast.error(error.message),
        },
      );
    }
  };

  return (
    <Flex column gap={16} alignItems="flex-start">
      <SessionList onRevoke={askToRevoke} onViewDetails={viewDetails} />
      <Button
        buttonType="secondary"
        buttonStyle="destructive"
        onClick={() => askToRevoke("others")}
        loading={isRevokingOthers}
        disabled={!hasOtherSessions}
      >
        Sign out all other sessions
      </Button>
      <SessionDetailsModal session={viewing} open={isDetailsOpen} onOpenChange={setIsDetailsOpen} />
      <ConfirmModal
        open={isConfirmOpen}
        onOpenChange={setIsConfirmOpen}
        onConfirm={confirmRevoke}
        confirmLabel={revoking === "others" ? "Sign out all" : "Revoke"}
        message={
          revoking === "others"
            ? `Sign out of every other device? ${REVOKE_OTHERS_DELAY_MESSAGE}`
            : `Sign out ${describeUserAgent(revoking?.userAgent)}? ${REVOKE_DELAY_MESSAGE}`
        }
      />
    </Flex>
  );
}

export default ActiveSessionsSection;

const List = styled.ul(({ theme }) => ({
  listStyle: "none",
  margin: 0,
  padding: 0,
  width: "100%",
  border: `1px solid ${theme.colors.sysBorderDecorative}`,
  borderRadius: 8,
}));

const Row = styled.li(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: 12,
  padding: "12px 16px",
  "&:not(:last-of-type)": {
    borderBottom: `1px solid ${theme.colors.sysBorderDecorative}`,
  },
}));

const Details = styled.dl(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "auto 1fr",
  columnGap: 16,
  rowGap: 8,
  margin: 0,
  "& dt": {
    color: theme.colors.sysTextSecondary,
  },
  "& dd": {
    margin: 0,
    overflowWrap: "anywhere",
  },
}));
